import { FaFacebook, FaInstagram, FaTiktok, FaXTwitter, FaThreads } from "react-icons/fa6";
import { SITE_NAME, SOCIAL_PROFILES } from "../../config/site";

// One icon per network in SOCIAL_PROFILES. A profile whose network has no
// icon here still renders in the "list" variant (as text), and is skipped in
// the "icons" variant rather than shown as an empty, unlabelled link.
const ICONS = {
  Facebook: FaFacebook,
  Instagram: FaInstagram,
  TikTok: FaTiktok,
  X: FaXTwitter,
  Threads: FaThreads,
};

/**
 * Emporia's official social accounts, read from SOCIAL_PROFILES only.
 *
 * Every link opens in a new tab with rel="noopener noreferrer", and carries
 * an accessible name that says whose account it is ("Emporia on Instagram")
 * — an icon alone announces as nothing, and "Instagram" alone sounds like a
 * link to the network.
 *
 * variant="icons": a row of icons (the footer).
 * variant="list":  icon + network + handle, for the contact blocks, where a
 *                  customer checking "is this account really you?" needs to
 *                  read the handle, not recognise a logo. No aria-label
 *                  there: the visible text already names the account, and
 *                  an override that differs from it fails label-in-name.
 */
const SocialLinks = ({ variant = "icons", className = "" }) => {
  if (!SOCIAL_PROFILES || SOCIAL_PROFILES.length === 0) return null;

  if (variant === "list") {
    return (
      <ul className={`social-list ${className}`.trim()}>
        {SOCIAL_PROFILES.map(({ label, handle, href }, i) => {
          const Icon = ICONS[label];
          return (
            <li key={label}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="cd-link social-list__link"
              >
                {Icon ? <Icon aria-hidden="true" focusable="false" /> : null}
                <span>{label}</span>
                {handle ? <span className="social-list__handle"> {handle}</span> : null}
              </a>
              {/* A real text node between accounts, not just the flex gap:
                  text extraction (search snippets, copy-and-paste, screen
                  readers) otherwise reads "…@emporia.keTikTok…". Same rule
                  as ContactDetails' separators. */}
              {i < SOCIAL_PROFILES.length - 1 ? <span className="cd-sep" aria-hidden="true"> · </span> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className={className}>
      {SOCIAL_PROFILES.map(({ label, href }) => {
        const Icon = ICONS[label];
        return Icon ? (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${SITE_NAME} on ${label}`}
          >
            <Icon aria-hidden="true" focusable="false" />
          </a>
        ) : null;
      })}
    </div>
  );
};

export default SocialLinks;
